import {
  ApiChartYAxisKey,
  ApiDimension,
  ApiChartDisplayOptions,
  ApiChartTemplate,
} from "@incendium/api";
import { groupBy } from "Helpers/arrays";
import { IChartData, TChartData } from "Interfaces";
import { useFromToContext } from "Providers/FromToProvider";
import {
  filterNonTrendDimensions,
  getUniqueMetrics,
  mergeApiChartYAxisKeys,
  mergeDimensionToMetrics,
  remapYaxisFromUniqueMetrics,
  removeFirstDimension,
} from "../utils/utils";
import { memo, useMemo } from "react";
import StyledComposedChart from "./StyledComposedChart";

interface IAnalyticsComposedBridgeProps {
  displayOptions?: ApiChartDisplayOptions;
  chartData: IChartData | null;
  comparisonChartData?: IChartData;
  dimensions: ApiDimension[];
  chartTemplate?: ApiChartTemplate;
  customDataFn?: (arr: TChartData[]) => TChartData[];
  verticalReferenceLines?: Array<{
    x: string;
    label?: string | React.ReactElement;
    stroke?: string;
  }>;
}

function bridgeChartData(
  chartData: IChartData,
  dimensions: ApiDimension[],
  lastNonTrendDimension: ApiDimension | undefined,
): IChartData {
  // due to back compatability issues we convert the first dimension to name, becouse of this we need to remove the fist dimension
  const data = removeFirstDimension(chartData.data, dimensions);
  if (dimensions.length === 1) {
    return {
      data,
      y: chartData.y,
    };
  }

  // for backwards compatabilty, we split second dimension in a chart to multiple metrics,
  // e.g say second dimension is channel and the channels are direct and organic, and metric is lead_count, we create direct//lead_count and organic//lead_count
  const groupedData = groupBy(data, "name");
  const trendData = mergeDimensionToMetrics(
    groupedData,
    lastNonTrendDimension as ApiDimension,
  );

  const uniqueMetrics = getUniqueMetrics(trendData);

  return {
    data: trendData,
    y: remapYaxisFromUniqueMetrics(
      (chartData.y || []) as ApiChartYAxisKey[],
      uniqueMetrics,
    ),
  };
}

function AnalyticsComposedBridge({
  displayOptions,
  chartData,
  comparisonChartData,
  dimensions,
  chartTemplate,
  customDataFn,
  verticalReferenceLines = [],
}: IAnalyticsComposedBridgeProps) {
  const { comparisonWindowDays } = useFromToContext();

  const lastNonTrendDimension = useMemo(() => {
    const filtered = filterNonTrendDimensions(dimensions);
    return filtered[filtered.length - 1];
  }, [dimensions]);

  const bridgedChartData = useMemo(() => {
    if (!chartData) {
      return null;
    }
    return bridgeChartData(chartData, dimensions, lastNonTrendDimension);
  }, [chartData, dimensions, lastNonTrendDimension]);

  const bridgedComparisonChartData = useMemo(() => {
    if (!comparisonChartData) {
      return undefined;
    }
    const data = removeFirstDimension(comparisonChartData.data, dimensions);
    const groupedData = groupBy(data, "name");
    const remappedData = mergeDimensionToMetrics(
      groupedData,
      lastNonTrendDimension,
      "previous_",
    );

    const uniqueMetrics = getUniqueMetrics(remappedData);

    return {
      data: remappedData,
      y: remapYaxisFromUniqueMetrics(
        (comparisonChartData.y || []) as ApiChartYAxisKey[],
        uniqueMetrics,
      ),
    };
  }, [comparisonChartData, dimensions, lastNonTrendDimension]);

  const mergedYAxis = useMemo(
    () =>
      mergeApiChartYAxisKeys(
        (bridgedChartData?.y || []) as ApiChartYAxisKey[],
        (bridgedComparisonChartData?.y || []) as ApiChartYAxisKey[],
      ),
    [bridgedChartData, bridgedComparisonChartData],
  );

  const comparisonWindow = useMemo(() => {
    if (
      !bridgedComparisonChartData ||
      chartTemplate !== ApiChartTemplate.TREND
    ) {
      return;
    }
    return comparisonWindowDays;
  }, [bridgedComparisonChartData, chartTemplate, comparisonWindowDays]);

  if (!bridgedChartData) {
    return <></>;
  }

  return (
    <StyledComposedChart
      // Remount when bridged series change so Recharts picks up new composed series
      // (previously deferred via setTimeout after async state).
      key={`${dimensions.join("|")}:${bridgedChartData.data
        .map((row) => row.name)
        .join("|")}`}
      data={
        customDataFn
          ? customDataFn(bridgedChartData.data)
          : bridgedChartData.data
      }
      yAxisKeys={mergedYAxis}
      comparismentChart={bridgedComparisonChartData}
      noLegend={displayOptions?.noLegend}
      noReOrder={displayOptions?.noReorder}
      metricsOrder={displayOptions?.metricsOrder}
      layout={displayOptions?.layout}
      fills={displayOptions?.fills}
      showTooltip
      dimensions={dimensions}
      comparisonWindowDays={comparisonWindow}
      noAxis={displayOptions?.noAxis}
      verticalReferenceLines={
        chartTemplate === ApiChartTemplate.TREND ? verticalReferenceLines : []
      }
    />
  );
}

export default memo(AnalyticsComposedBridge);
